import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

/** Short date for lists (e.g. email templates): "14 fév. 2026" in French */
export function formatShortListDate(date: string | Date, locale?: string): string {
  const d = new Date(date)
  const lang = locale || (typeof navigator !== 'undefined' ? navigator.language : 'en')
  if (lang.startsWith('fr')) {
    return d
      .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
      .replace(/\u202f/g, ' ')
      .replace('févr.', 'fév.')
  }
  return d.toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Time for stats headers, e.g. French "11 h 17" */
export function formatStatsTime(date: Date, locale?: string): string {
  const lang = locale || (typeof navigator !== 'undefined' ? navigator.language : 'en')
  const loc = lang.startsWith('fr') ? 'fr-FR' : lang
  const s = date.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', hour12: false })
  if (lang.startsWith('fr')) {
    return s.replace(':', ' h ')
  }
  return s
}

/** Date and time for notifications, e.g. "Jun 15, 2026, 11:30 AM" */
export function formatNotificationDateTime(date: string | Date | null | undefined, locale?: string): string {
  if (!date) return ''
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  const lang = locale || (typeof navigator !== 'undefined' ? navigator.language : 'en')
  return d.toLocaleString(lang, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatCurrency(amount: string): string {
  return amount
}

export function generateMatchScore(): number {
  return Math.floor(Math.random() * 41) + 60 // Random score between 60-100
}

export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'applied':
      return 'bg-blue-500 text-white dark:bg-blue-900 dark:text-blue-300'
    case 'interview':
      return 'bg-yellow-500 text-white dark:bg-yellow-900 dark:text-yellow-300'
    case 'hr_interview':
      return 'bg-purple-500 text-white dark:bg-purple-900 dark:text-purple-300'
    case 'offer':
      return 'bg-green-500 text-white dark:bg-green-900 dark:text-green-300'
    case 'hired':
      return 'bg-emerald-500 text-white dark:bg-emerald-900 dark:text-emerald-300'
    case 'rejected':
      return 'bg-red-500 text-white dark:bg-red-900 dark:text-red-300'
    case 'draft':
      return 'bg-gray-500 text-white dark:bg-gray-900 dark:text-gray-300'
    case 'published':
      return 'bg-green-500 text-white dark:bg-green-900 dark:text-green-300'
    case 'offer_closed':
      return 'bg-orange-500 text-white dark:bg-orange-900 dark:text-orange-300'
    case 'closed':
      return 'bg-red-500 text-white dark:bg-red-900 dark:text-red-300'
    // Legacy statuses for backward compatibility
    case 'pending':
      return 'bg-orange-500 text-white dark:bg-orange-900 dark:text-orange-300'
    case 'accepted':
      return 'bg-green-500 text-white dark:bg-green-900 dark:text-green-300'
    case 'declined':
      return 'bg-red-500 text-white dark:bg-red-900 dark:text-red-300'
    default:
      return 'bg-gray-500 text-white dark:bg-gray-900 dark:text-gray-300'
  }
}

export function calculateDaysSince(date: string): number {
  const now = new Date()
  const then = new Date(date)
  const diffTime = Math.abs(now.getTime() - then.getTime())
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
}

export function exportToCSV(data: any[], filename: string): void {
  const csvContent = [
    Object.keys(data[0]).join(','),
    ...data.map(row => Object.values(row).join(','))
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.csv`
  link.click()
  window.URL.revokeObjectURL(url)
}

/**
 * Base URL for media (candidate images, etc.). Media is always served by the API server.
 * In dev, frontend is often on :3000 and backend on :8000 – we must use :8000 for media.
 */
function getMediaBaseUrl(): string {
  const envUrl = typeof process !== 'undefined' && (process as any).env?.REACT_APP_API_URL
  if (envUrl && envUrl.trim().length > 0) {
    return envUrl.trim().replace(/\/+$/, '').replace(/\/api\/?$/, '')
  }
  if (typeof window !== 'undefined') {
    const port = window.location.port || ''
    // Frontend dev server is usually 3000/3001; media is on backend (8000). Never use frontend origin for /media.
    if (port === '3000' || port === '3001') {
      return `${window.location.protocol}//${window.location.hostname}:8000`
    }
    // Same-origin (e.g. production or backend and frontend on same host)
    const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : ''
    return `${window.location.protocol}//${window.location.hostname}${portSuffix}`
  }
  return 'http://localhost:8000'
}

/**
 * Converts a relative or localhost media URL to an absolute URL.
 * Preserves signed ?token= query params from the API.
 * Works for images, videos, and other /media/ assets.
 */
export function getImageUrl(url: string | null | undefined): string | null {
  return resolveMediaUrl(url)
}

export function getMediaUrl(url: string | null | undefined): string | null {
  return resolveMediaUrl(url)
}

function resolveMediaUrl(url: string | null | undefined): string | null {
  if (!url) return null

  const mediaBaseUrl = getMediaBaseUrl()
  const normalizedUrl = url.trim()

  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    const hasLocalhost = /localhost|127\.0\.0\.1|backend/i.test(normalizedUrl)
    if (hasLocalhost) {
      try {
        const urlObj = new URL(normalizedUrl)
        const path = urlObj.pathname + urlObj.search + urlObj.hash
        return `${mediaBaseUrl}${path}`
      } catch {
        let replaced = normalizedUrl.replace(/https?:\/\/[^/]+/, mediaBaseUrl)
        if (/localhost|127\.0\.0\.1|backend/i.test(replaced)) {
          replaced = replaced.replace(/https?:\/\/(localhost|127\.0\.0\.1|backend)(:\d+)?/i, mediaBaseUrl)
        }
        return replaced
      }
    }
    return normalizedUrl
  }

  const imagePath = normalizedUrl.startsWith('/') ? normalizedUrl : `/${normalizedUrl}`
  return `${mediaBaseUrl}${imagePath}`
}
